import React, { useState } from 'react';
import { ShieldCheck, ShieldAlert, ChevronDown, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
export function Hero() {
  const [patientType, setPatientType] = useState<'new' | 'existing'>('new');
  const visitReasons = ['Braces / Retainer Checkup', 'Braces Consultation', 'Clear Aligners / Invisalign Consultation', 'Install Braces', 'Retainer Installation', 'Braces / Clear Aligners / Retainer Removal', 'Braces / Clear Aligners Repair or Replacement', 'Gapped Teeth', 'Install Clear Aligners / Invisalign', 'Lingual Bar Retainers', 'Retainer Checkup'];
  const insurances = ['Aetna', 'BlueCross BlueShield', 'Cigna', 'Delta Dental', 'Guardian', 'Metlife', 'DC Medicaid'];
  return <section id="about" className="relative pt-48 md:pt-40 pb-24 px-4 overflow-hidden" data-id="element-142">
      {/* Video Background */}
      <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" data-id="element-143">
        <source src="https://cdn.magicpatterns.com/uploads/eN5ftQz7jXFdUwThVwRk5R/6529141-hd_2048_1080_25fps.mp4" type="video/mp4" data-id="element-144" />
      </video>

      {/* Dark Overlay for Text Readability */}
      <div className="absolute inset-0 bg-[#0F2C59]/80 mix-blend-multiply" data-id="element-145" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F2C59]/90 via-[#0F2C59]/60 to-[#0F2C59]/90" data-id="element-146" />

      <div className="relative z-10 max-w-7xl mx-auto text-center" data-id="element-147">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight" data-id="element-148">
          DENTAL CARE,
          <br data-id="element-149" />
          BUILT AROUND YOU.
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-xl text-white/90 max-w-3xl mx-auto mb-16" data-id="element-150">
          Accepting All Patients. We proudly cater to Medicaid and Public Aid as
          well as Private Insurance providers.
        </motion.p>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-151">
          {/* Card A: Stacked Clinic Images */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} className="relative h-full min-h-[400px] flex items-center justify-center group perspective-1000" data-id="element-152">
            {/* Back Card */}
            <div className="absolute w-64 h-80 rounded-[12px] overflow-hidden shadow-2xl border-4 border-white transform transition-all duration-500 -rotate-12 -translate-x-12 group-hover:-rotate-12 group-hover:-translate-x-20 z-10" data-id="element-153">
              <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&q=80&w=600" alt="Dental Clinic Room" className="w-full h-full object-cover" data-id="element-154" />
              <div className="absolute inset-0 bg-[#0F2C59]/20 mix-blend-multiply" data-id="element-155" />
            </div>

            {/* Middle Card */}
            <div className="absolute w-64 h-80 rounded-[12px] overflow-hidden shadow-2xl border-4 border-white transform transition-all duration-500 rotate-12 translate-x-12 group-hover:rotate-12 group-hover:translate-x-20 z-20" data-id="element-156">
              <img src="https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&q=80&w=600" alt="Dental Tools" className="w-full h-full object-cover" data-id="element-157" />
              <div className="absolute inset-0 bg-[#0F2C59]/20 mix-blend-multiply" data-id="element-158" />
            </div>

            {/* Front Card */}
            <div className="absolute w-72 h-96 rounded-[12px] overflow-hidden shadow-3xl border-4 border-white transform transition-all duration-500 z-30 group-hover:-translate-y-4" data-id="element-159">
              <img src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&q=80&w=600" alt="Happy Patient" className="w-full h-full object-cover" data-id="element-160" />

              {/* Overlay Badge */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0F2C59] to-transparent p-6 pt-12" data-id="element-161">
                <div className="flex items-center gap-2 mb-2" data-id="element-162">
                  <ShieldAlert className="text-[#0D9488]" size={20} data-id="element-163" />
                  <span className="text-white font-bold text-sm tracking-wider uppercase" data-id="element-164">
                    Medicaid Accepted
                  </span>
                </div>
                <p className="text-white/90 text-sm leading-tight" data-id="element-165">
                  We believe everyone deserves a healthy smile with no hidden
                  fees.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Card B: Booking Widget */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="bg-[#0F2C59] border-2 border-white/10 rounded-[12px] p-8 shadow-2xl hover:shadow-3xl transition-shadow text-left text-white flex flex-col justify-between backdrop-blur-sm" data-id="element-166">
            <div data-id="element-167">
              <div className="flex items-center gap-3 mb-6" data-id="element-168">
                <div className="w-12 h-12 bg-[#0D9488]/20 rounded-full flex items-center justify-center" data-id="element-169">
                  <Calendar className="text-[#0D9488]" size={24} data-id="element-170" />
                </div>
                <h3 className="text-2xl font-bold" data-id="element-171">Book an appointment</h3>
              </div>

              {/* Patient Type Toggle */}
              <div className="flex bg-white/10 rounded-lg p-1 mb-6" data-id="element-172">
                <button onClick={() => setPatientType('new')} className={`flex-1 py-2 text-sm font-bold rounded-md transition-colors ${patientType === 'new' ? 'bg-[#0D9488] text-white shadow-sm' : 'text-white/70 hover:text-white'}`} data-id="element-173">
                  New patient
                </button>
                <button onClick={() => setPatientType('existing')} className={`flex-1 py-2 text-sm font-bold rounded-md transition-colors ${patientType === 'existing' ? 'bg-[#0D9488] text-white shadow-sm' : 'text-white/70 hover:text-white'}`} data-id="element-174">
                  Existing patient
                </button>
              </div>

              <div className="space-y-4 mb-8" data-id="element-175">
                {/* Reason Dropdown */}
                <div className="relative" data-id="element-176">
                  <label className="block text-xs font-bold text-white/70 mb-1 uppercase tracking-wider" data-id="element-177">
                    Reason for visit
                  </label>
                  <select className="w-full bg-white/5 border border-white/20 rounded-lg py-3 px-4 appearance-none text-white focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent transition-all" data-id="element-178">
                    <option value="" disabled selected className="text-slate-800" data-id="element-179">
                      Select reason...
                    </option>
                    {visitReasons.map((reason, idx) => <option key={idx} value={reason} className="text-slate-800" data-id="element-180">
                        {reason}
                      </option>)}
                  </select>
                  <ChevronDown className="absolute right-4 top-[34px] text-white/50 pointer-events-none" size={18} data-id="element-181" />
                </div>

                {/* Insurance Dropdown */}
                <div className="relative" data-id="element-182">
                  <label className="block text-xs font-bold text-white/70 mb-1 uppercase tracking-wider" data-id="element-183">
                    Insurance carrier and plan
                  </label>
                  <select className="w-full bg-white/5 border border-white/20 rounded-lg py-3 px-4 appearance-none text-white focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent transition-all" data-id="element-184">
                    <option value="" disabled selected className="text-slate-800" data-id="element-185">
                      Select insurance...
                    </option>
                    {insurances.map((ins, idx) => <option key={idx} value={ins} className="text-slate-800" data-id="element-186">
                        {ins}
                      </option>)}
                    <option value="other" className="text-slate-800" data-id="element-187">
                      Other / Self-Pay
                    </option>
                  </select>
                  <ChevronDown className="absolute right-4 top-[34px] text-white/50 pointer-events-none" size={18} data-id="element-188" />
                </div>
              </div>
            </div>

            <button className="w-full bg-[#0D9488] text-white font-bold py-4 rounded-lg hover:bg-[#0F766E] transition-colors shadow-lg shadow-teal-900/20 flex items-center justify-center gap-2" data-id="element-189">
              <Calendar size={18} data-id="element-190" />
              Find Available Times
            </button>
          </motion.div>
        </div>
      </div>
    </section>;
}